import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import api from "@/lib/api";

const DropArchive = () => {
  const [drops, setDrops] = useState([]);

  useEffect(() => {
    api.get("/drops").then((r) => setDrops(r.data));
  }, []);

  return (
    <div data-testid="drops-page">
      {/* HERO */}
      <section className="bg-[#1C1C1C] text-[#FDFBF7] grain relative overflow-hidden border-b-2 border-[#1C1C1C]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <span className="inline-block bg-[#D35400] text-white text-xs font-bold uppercase tracking-[0.25em] px-3 py-1.5 mb-6">
            The Vault
          </span>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">Monthly Drop<br />Archive</h1>
          <p className="text-xl text-[#C9C4BA] mt-6 max-w-2xl leading-relaxed">
            Every month we design and print a brand-new tribute to Kansas City. Here's the vault — every past Tee Club
            drop, the story behind it, and the designs our members wear with pride.
          </p>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="space-y-16 md:space-y-24">
          {drops.map((d, i) => (
            <motion.div
              key={d.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}
              data-testid={`drop-${d.id}`}
            >
              <div className={`${i % 2 === 1 ? "md:[direction:ltr]" : ""}`}>
                <div className={`border-2 border-[#1C1C1C] hard-shadow-lg bg-[#F5F2EA] p-3 ${i % 2 === 0 ? "-rotate-1" : "rotate-1"}`}>
                  <img src={d.image} alt={d.name} className="w-full aspect-square object-cover" loading="lazy" />
                </div>
              </div>
              <div className={`${i % 2 === 1 ? "md:[direction:ltr]" : ""}`}>
                <div className="flex items-center gap-2 text-[#D35400] font-bold uppercase tracking-[0.2em] text-sm">
                  <Calendar className="w-4 h-4" strokeWidth={2.5} />
                  {d.month}
                </div>
                <h2 className="font-display text-5xl md:text-6xl mt-3 leading-none">{d.name}</h2>
                {d.sold_out && (
                  <span className="inline-block mt-3 bg-[#C0392B] text-white text-xs font-bold uppercase tracking-widest px-2 py-1">
                    Sold Out
                  </span>
                )}
                <p className="mt-5 text-lg text-[#4A4A4A] leading-relaxed">{d.story}</p>
                <Link
                  to="/tee-club"
                  className="btn-primary px-7 py-3.5 mt-7 inline-flex items-center gap-2"
                  data-testid={`drop-join-${d.id}`}
                >
                  Join the Tee Club <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#D35400] text-white py-20 md:py-24 grain relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <h2 className="font-display text-5xl md:text-6xl mb-4">Don't Miss The Next One</h2>
          <p className="text-lg mb-8 text-white/90">
            Past drops are gone for good. Join the Tee Club and never miss a piece of Kansas City history again.
          </p>
          <Link
            to="/tee-club"
            className="bg-[#1C1C1C] text-white px-8 py-4 font-bold uppercase tracking-widest inline-block hover:bg-black transition"
            data-testid="drops-cta-join"
          >
            Join the Tee Club — $24/mo
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DropArchive;
